import { filterEncoders, serializeFilters } from '@utils/serialize-filters';

import type {
  ApplicationFilters,
  AssetsListFilters,
  ComplianceFilters,
  ContentsListFilters,
  VersionFilters,
  VulnerabilitiesFilters,
} from '../applications.models';

/**
 * Translates client filter state into the query params consumed by the Node
 * service wrapper. Annotations are JSON-stringified so user-defined keys cross
 * the HTTP boundary as string content (not URL object keys), preserving case.
 */
export const serializeAssetsListFilters = (filters?: AssetsListFilters) =>
  serializeFilters(filters, {
    annotations: filterEncoders.jsonRecord,
    createdAt: filterEncoders.dateRange('created'),
    name: filterEncoders.string,
    type: filterEncoders.stringArray,
  });

/**
 * Translates Application list filter state into the query params consumed by
 * the Node service wrapper. `DateRangeValue` filters expand into
 * `${prefix}After`/`${prefix}Before` ISO bounds so the search endpoint can
 * build `created_at` / `latest_version.created_at` range conditions without
 * re-parsing the relative-date format.
 */
export const serializeApplicationsListFilters = (
  filters?: ApplicationFilters,
) =>
  serializeFilters(filters, {
    name: filterEncoders.string,
    description: filterEncoders.string,
    contactName: filterEncoders.string,
    contactEmail: filterEncoders.string,
    minVersions: filterEncoders.number,
    maxVersions: filterEncoders.number,
    createdAt: filterEncoders.dateRange('created'),
    latestVersionName: filterEncoders.string,
    latestVersionCreatedAt: filterEncoders.dateRange('latestVersionCreated'),
  });

/**
 * Translates client filter state for the version compliance (policy findings)
 * tab into the query params consumed by the Node service wrapper. Mirrors
 * {@link serializeVulnerabilitiesFilters}; the Node-side schema in
 * `policy-findings-filter-schema` decodes these back into platform search
 * filter conditions.
 */
export const serializeComplianceFilters = (filters?: ComplianceFilters) =>
  serializeFilters(filters, {
    action: filterEncoders.stringArray,
    allowlisted: filterEncoders.boolean,
    assetsAffectedMax: filterEncoders.number,
    assetsAffectedMin: filterEncoders.number,
    epssScoreMax: filterEncoders.number,
    epssScoreMin: filterEncoders.number,
    hasFix: filterEncoders.boolean,
    hasVexAnnotation: filterEncoders.boolean,
    kev: filterEncoders.boolean,
    policyRule: filterEncoders.string,
    severity: filterEncoders.stringArray,
    vexStatus: filterEncoders.stringArray,
    vulnerabilityId: filterEncoders.string,
  });

export const serializeContentsListFilters = (filters?: ContentsListFilters) =>
  serializeFilters(filters, {
    assetCountMax: filterEncoders.number,
    assetCountMin: filterEncoders.number,
    hasVulnerabilities: filterEncoders.boolean,
    licenses: filterEncoders.string,
    name: filterEncoders.string,
    purl: filterEncoders.string,
    severity: filterEncoders.stringArray,
    type: filterEncoders.stringArray,
    version: filterEncoders.string,
  });

/**
 * Translates Application Version list filter state into the query params
 * consumed by the Node service wrapper. `DateRangeValue` filters expand into
 * `${prefix}After`/`${prefix}Before` ISO bounds so the search endpoint can
 * build `release_date` / `created_at` range conditions without re-parsing
 * the relative-date format.
 */
export const serializeVersionsListFilters = (filters?: VersionFilters) =>
  serializeFilters(filters, {
    name: filterEncoders.string,
    description: filterEncoders.string,
    status: filterEncoders.stringArray,
    minAssets: filterEncoders.number,
    maxAssets: filterEncoders.number,
    releaseDate: filterEncoders.dateRange('released'),
    createdAt: filterEncoders.dateRange('created'),
  });

/**
 * Translates client filter state for the vulnerability list/summary into the
 * query params consumed by the Node service wrapper. The metrics summary and
 * the table query share this single entry point so they always send the same
 * filter shape. `lastFound` (DateRange) expands into
 * `lastFoundAfter`/`lastFoundBefore` ISO bounds for the platform.
 */
export const serializeVulnerabilitiesFilters = (
  filters?: VulnerabilitiesFilters,
) =>
  serializeFilters(filters, {
    affectedPackagesMax: filterEncoders.number,
    affectedPackagesMin: filterEncoders.number,
    anchoreScoreMax: filterEncoders.number,
    anchoreScoreMin: filterEncoders.number,
    cvssScoreMax: filterEncoders.number,
    cvssScoreMin: filterEncoders.number,
    epssScoreMax: filterEncoders.number,
    epssScoreMin: filterEncoders.number,
    fixState: filterEncoders.stringArray,
    hasFix: filterEncoders.boolean,
    hasVexAnnotation: filterEncoders.boolean,
    kev: filterEncoders.boolean,
    lastFound: filterEncoders.dateRange('lastFound'),
    severity: filterEncoders.stringArray,
    vexStatus: filterEncoders.stringArray,
    vulnerabilityId: filterEncoders.string,
  });
